import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Divider,
  ListItemIcon
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { brand } from '../../theme';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  const isSuper = user?.role === 'super_admin';

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        bgcolor: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${brand.border}`,
        color: brand.secondary
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 2, md: 3 },
          pl: { xs: 7, lg: 3 },
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box sx={{ minWidth: 0, display: { xs: 'none', md: 'block' } }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            Admin console
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: 'auto' }}>
          <Chip
            size="small"
            label={isSuper ? 'Super Admin' : user?.role || 'Admin'}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              fontWeight: 700,
              bgcolor: isSuper ? brand.primarySoft : '#f1f5f9',
              color: isSuper ? brand.primary : '#475569',
              border: `1px solid ${isSuper ? 'rgba(222,88,36,0.35)' : brand.border}`
            }}
          />

          <Box
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              px: 1,
              py: 0.5,
              borderRadius: 2.5,
              border: `1px solid ${brand.border}`,
              bgcolor: '#fff',
              transition: 'border-color 150ms ease, box-shadow 150ms ease',
              '&:hover': {
                borderColor: 'rgba(222,88,36,0.45)',
                boxShadow: '0 4px 14px rgba(15,23,42,0.06)'
              }
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: brand.primary,
                fontSize: 13,
                fontWeight: 700
              }}
            >
              {initials || 'A'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' }, pr: 0.25, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email || 'Admin'}
              </Typography>
            </Box>
            <KeyboardArrowDownRoundedIcon fontSize="small" sx={{ color: '#94a3b8' }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1,
                minWidth: 200,
                border: `1px solid ${brand.border}`,
                boxShadow: '0 12px 32px rgba(15,23,42,0.12)'
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/profile');
              }}
            >
              <ListItemIcon>
                <PersonRoundedIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                logout();
              }}
              sx={{ color: brand.error }}
            >
              <ListItemIcon>
                <LogoutRoundedIcon fontSize="small" sx={{ color: brand.error }} />
              </ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
